import { ChevronDown, ChevronRight, Loader2, Lock, Plus, Trash2, X, Zap } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormSelect } from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import i18n from "../../../i18n";
import { searchProductServicesPage } from "../../../services/fsmService";
import {
  fetchMetricCategories,
  fetchUomOptionsByCategory,
} from "../../../services/productCatalogService";
import {
  fetchCatalogProductById,
  fetchPartNumbersByMake,
} from "../../../services/quotationService";

/** Match a saved quotation UOM string to a metric option (abbr / name / label). */
function metricMatchesUom(metric, uomValue) {
  const target = String(uomValue || "")
    .trim()
    .toLowerCase();
  if (!target || !metric) return false;
  const candidates = [metric.abbreviation, metric.name, metric.label, metric.id]
    .map((part) => String(part || "").trim().toLowerCase())
    .filter(Boolean);
  return candidates.some((part) => part === target);
}

/** Persist compact UOM text on the line item (same idea as catalogue abbreviation/name). */
function uomValueFromMetric(metric) {
  if (!metric) return "";
  const abbreviation = String(metric.abbreviation || "").trim();
  if (abbreviation) return abbreviation;
  const name = String(metric.name || "").trim();
  if (name) return name;
  return String(metric.label || "").trim();
}

function sq(key, options) {
  return i18n.t(`salesQuotation.${key}`, options);
}

function Field({ label, children, hint }) {
  return (
    <div className="mb-3">
      <label className="mb-1.5 block text-[12px] font-semibold text-[#51657f]">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-[11px] text-[#8ea0bd]">{hint}</p> : null}
    </div>
  );
}

function mobileInputClass(readonly = false) {
  return `min-h-[44px] w-full rounded-[12px] border border-[#d1d9e8] bg-white px-3 text-[13px] font-normal text-[#2d394c] outline-hidden focus:border-[#95addd] ${
    readonly ? "bg-[#f8fbff] text-[#6f82a1]" : ""
  }`;
}

/** Ignore option select when the finger moved enough to count as a list scroll. */
const MOBILE_PICKER_TAP_MOVE_PX = 10;

function MobileSearchSheet({
  open,
  title,
  searchPlaceholder,
  query,
  onQueryChange,
  searchRef,
  onClose,
  children,
  localizedFieldProps,
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end bg-black/40"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="flex max-h-[80dvh] w-full flex-col rounded-t-[20px] bg-[#f4f6fb]"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 border-b border-[#e2e4f0] px-4 py-3">
          <p className="text-[16px] font-semibold text-[#23324c]">{title}</p>
          <button
            type="button"
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-full text-[#6f82a1]"
            aria-label={sq("close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="border-b border-[#e2e4f0] px-3 py-2.5">
          <input
            ref={searchRef}
            type="text"
            inputMode="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={query}
            onChange={(event) => onQueryChange?.(event.target.value)}
            placeholder={searchPlaceholder}
            className="min-h-[44px] w-full rounded-[12px] border border-[#d1d9e8] bg-white px-3 text-[14px] text-[#23324c] outline-hidden focus:border-[#95addd]"
            {...(localizedFieldProps?.() || {})}
          />
        </div>
        <div
          className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain px-3 py-3 [-webkit-overflow-scrolling:touch]"
          // Keep sheet open while the options list is scrolled; only backdrop closes.
          onPointerDown={(event) => event.stopPropagation()}
          onTouchMove={(event) => {
            event.stopPropagation();
            // Dismiss keyboard while scrolling so a later tap is not eaten by blur/resize.
            if (searchRef?.current && document.activeElement === searchRef.current) {
              searchRef.current.blur();
            }
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Option / clear row for Make / Part No sheets.
 * Select on tap (pointerup), not pointerdown — pointerdown+preventDefault was
 * selecting/closing the sheet as soon as a scroll gesture started on an option.
 */
function MobilePickerOption({ label, selected, onSelect, muted = false }) {
  const gestureRef = useRef({ active: false, x: 0, y: 0, moved: false, handled: false });

  return (
    <button
      type="button"
      onPointerDown={(event) => {
        event.stopPropagation();
        // Do not preventDefault here — that blocks touch scrolling inside the list.
        gestureRef.current = {
          active: true,
          x: event.clientX,
          y: event.clientY,
          moved: false,
          handled: false,
        };
      }}
      onPointerMove={(event) => {
        const gesture = gestureRef.current;
        if (!gesture.active || gesture.moved) return;
        if (
          Math.abs(event.clientX - gesture.x) > MOBILE_PICKER_TAP_MOVE_PX ||
          Math.abs(event.clientY - gesture.y) > MOBILE_PICKER_TAP_MOVE_PX
        ) {
          gesture.moved = true;
        }
      }}
      onPointerUp={(event) => {
        event.stopPropagation();
        const gesture = gestureRef.current;
        if (!gesture.active) return;
        gesture.active = false;
        if (gesture.moved) return;
        gesture.handled = true;
        onSelect?.();
      }}
      onPointerCancel={() => {
        gestureRef.current = { active: false, x: 0, y: 0, moved: false, handled: false };
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        const gesture = gestureRef.current;
        // pointerup already selected — swallow the synthetic click.
        if (gesture.handled) {
          gesture.handled = false;
          return;
        }
        // Scroll gesture — do not select.
        if (gesture.moved) {
          gesture.moved = false;
          return;
        }
        // Keyboard / click-only activation.
        onSelect?.();
      }}
      className={`block w-full rounded-[12px] border px-4 py-3.5 text-left transition ${
        muted
          ? "border-[#e4e9f2] bg-white text-[13px] font-semibold text-[#6f82a1]"
          : selected
            ? "border-[#243c93] bg-[#eef2ff]"
            : "border-[#d1d9e8] bg-white active:bg-[#f7f9ff]"
      }`}
    >
      {muted ? (
        label
      ) : (
        <p className="text-[14px] font-semibold text-[#23324c]">{label}</p>
      )}
    </button>
  );
}

/**
 * Mobile Make picker — searchable bottom sheet (avoids shared FormSelect on mobile).
 */
function MobileMakeField({
  value,
  makes = [],
  makesLoading,
  editable,
  onChange,
  localizedFieldProps,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const selectingRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    selectingRef.current = false;
    const timer = window.setTimeout(() => searchRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [open]);

  const makeOptions = useMemo(() => {
    const list = Array.isArray(makes) ? makes : [];
    const names = list
      .map((make) => String(make?.name || "").trim())
      .filter(Boolean);
    if (value && !names.some((name) => name === value)) {
      return [String(value), ...names];
    }
    return names;
  }, [makes, value]);

  const filteredOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return makeOptions;
    return makeOptions.filter((name) => name.toLowerCase().includes(term));
  }, [makeOptions, query]);

  function handleSelect(nextValue) {
    if (selectingRef.current) return;
    selectingRef.current = true;
    const next = String(nextValue || "").trim();
    onChange?.(next);
    setOpen(false);
    setQuery("");
    window.setTimeout(() => {
      selectingRef.current = false;
    }, 0);
  }

  if (!editable) {
    return (
      <div className="flex min-h-[40px] items-center px-2 text-[13px] text-[#34445f]">
        {value || "-"}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        disabled={makesLoading}
        onClick={() => setOpen(true)}
        className="flex min-h-[40px] w-full items-center justify-between gap-2 rounded-[10px] bg-white px-2 text-left text-[13px] text-[#34445f] disabled:cursor-not-allowed disabled:text-[#8ea0bd]"
      >
        <span className="min-w-0 truncate">
          {value || (makesLoading ? "Loading..." : "Select make")}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-[#8ea0bd]" />
      </button>

      <MobileSearchSheet
        open={open}
        title="Make"
        searchPlaceholder="Search make..."
        query={query}
        onQueryChange={setQuery}
        searchRef={searchRef}
        onClose={() => {
          if (!selectingRef.current) setOpen(false);
        }}
        localizedFieldProps={localizedFieldProps}
      >
        {makesLoading ? (
          <p className="px-1 py-6 text-center text-[13px] text-[#6f82a1]">Loading...</p>
        ) : null}
        {!makesLoading && filteredOptions.length === 0 ? (
          <p className="px-1 py-6 text-center text-[13px] text-[#6f82a1]">
            {query.trim() ? "No matching makes." : "No makes found"}
          </p>
        ) : null}
        <div className="space-y-2">
          {value ? (
            <MobilePickerOption
              label="Clear selection"
              muted
              onSelect={() => handleSelect("")}
            />
          ) : null}
          {filteredOptions.map((name) => (
            <MobilePickerOption
              key={name}
              label={name}
              selected={name === String(value || "")}
              onSelect={() => handleSelect(name)}
            />
          ))}
        </div>
      </MobileSearchSheet>
    </>
  );
}

/**
 * Mobile Part No picker — own bottom sheet + search (does not use shared FormSelect,
 * which closes on mobile keyboard / search focus).
 */
function MobilePartNoField({
  value,
  make,
  editable,
  onChange,
  onProductSelect,
  localizedFieldProps,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const searchRef = useRef(null);
  const selectingRef = useRef(false);
  const hydratedPartRef = useRef("");
  const selectedMake = String(make || "").trim();

  useEffect(() => {
    if (!selectedMake) {
      setOptions([]);
      setError("");
      setLoading(false);
      hydratedPartRef.current = "";
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    fetchPartNumbersByMake(selectedMake)
      .then((products) => {
        if (!cancelled) setOptions(Array.isArray(products) ? products : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setOptions([]);
        setError(err.message || "Unable to load part numbers.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedMake]);

  useEffect(() => {
    if (!onProductSelect) return;
    const partNumber = String(value || "").trim();
    if (!partNumber || !options.length) return;
    const hydrateKey = `${selectedMake}|${partNumber}`;
    if (hydratedPartRef.current === hydrateKey) return;

    const matched = options.find(
      (item) => String(item.partNumber || "").trim() === partNumber
    );
    if (!matched) return;

    let cancelled = false;
    hydratedPartRef.current = hydrateKey;

    (async () => {
      let resolved = matched;
      if (matched.id) {
        try {
          const detailed = await fetchCatalogProductById(matched.id);
          if (detailed) resolved = detailed;
        } catch {
          resolved = matched;
        }
      }
      if (!cancelled) onProductSelect(resolved, { replace: false });
    })();

    return () => {
      cancelled = true;
    };
  }, [options, value, selectedMake, onProductSelect]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    selectingRef.current = false;
    const timer = window.setTimeout(() => searchRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [open]);

  const filteredOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((product) =>
      String(product.partNumber || "")
        .toLowerCase()
        .includes(term)
    );
  }, [options, query]);

  async function handleSelect(partNumber) {
    if (selectingRef.current) return;
    selectingRef.current = true;
    const next = String(partNumber || "").trim();

    try {
      // Close sheet first so keyboard/blur cannot cancel the selection gesture.
      setOpen(false);
      setQuery("");
      onChange?.(next);

      if (!next) {
        onProductSelect?.(null);
        return;
      }

      hydratedPartRef.current = `${selectedMake}|${next}`;
      const matched = options.find(
        (item) => String(item.partNumber || "").trim() === next
      );
      if (!matched) {
        onProductSelect?.({ partNumber: next, make: selectedMake }, { replace: true });
        return;
      }

      let resolved = matched;
      if (matched.id) {
        try {
          const detailed = await fetchCatalogProductById(matched.id);
          if (detailed) resolved = detailed;
        } catch {
          resolved = matched;
        }
      }
      onProductSelect?.(resolved, { replace: true });
    } finally {
      window.setTimeout(() => {
        selectingRef.current = false;
      }, 0);
    }
  }

  if (!editable) {
    return (
      <div className="flex min-h-[40px] items-center px-2 text-[13px] text-[#34445f]">
        {value || "-"}
      </div>
    );
  }

  const disabled = !selectedMake || loading;

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        title={error || value || undefined}
        className="flex min-h-[40px] w-full items-center justify-between gap-2 rounded-[10px] bg-white px-2 text-left text-[13px] text-[#34445f] disabled:cursor-not-allowed disabled:text-[#8ea0bd]"
      >
        <span className="min-w-0 truncate">
          {value ||
            (!selectedMake
              ? "Select make first"
              : loading
                ? "Loading..."
                : options.length
                  ? "Select part no."
                  : "No parts found")}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-[#8ea0bd]" />
      </button>

      <MobileSearchSheet
        open={open}
        title="Part No"
        searchPlaceholder="Search part number..."
        query={query}
        onQueryChange={setQuery}
        searchRef={searchRef}
        onClose={() => {
          if (!selectingRef.current) setOpen(false);
        }}
        localizedFieldProps={localizedFieldProps}
      >
        {error ? (
          <p className="mb-3 rounded-[10px] border border-[#f4cfcb] bg-[#fff6f5] px-3 py-2 text-[12px] text-[#b04d43]">
            {error}
          </p>
        ) : null}
        {loading ? (
          <p className="px-1 py-6 text-center text-[13px] text-[#6f82a1]">Loading...</p>
        ) : null}
        {!loading && filteredOptions.length === 0 ? (
          <p className="px-1 py-6 text-center text-[13px] text-[#6f82a1]">
            {query.trim() ? "No matching part numbers." : "No parts found"}
          </p>
        ) : null}
        <div className="space-y-2">
          {value ? (
            <MobilePickerOption
              label="Clear selection"
              muted
              onSelect={() => {
                void handleSelect("");
              }}
            />
          ) : null}
          {filteredOptions.map((product) => {
            const partNumber = String(product.partNumber || "").trim();
            return (
              <MobilePickerOption
                key={product.id || partNumber}
                label={partNumber}
                selected={partNumber === String(value || "").trim()}
                onSelect={() => {
                  void handleSelect(partNumber);
                }}
              />
            );
          })}
        </div>
      </MobileSearchSheet>
    </>
  );
}

/**
 * Mobile UOM — single cascading tree dropdown (same flow as Product Catalogue
 * MetricTreeSelect): expand category → pick metric inside one sheet.
 * Roots: GET /product-metrics/categories (same as Product Catalogue)
 * Children: GET /product-metrics/search/category
 */
function MobileUomField({
  value,
  metricCategory: metricCategoryProp = "",
  metricId: metricIdProp = "",
  editable,
  onChange,
  localizedFieldProps,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [childrenByCategory, setChildrenByCategory] = useState({});
  const [loadingCategory, setLoadingCategory] = useState(null);
  const [childError, setChildError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(
    String(metricCategoryProp || "").trim()
  );
  const [selectedMetricId, setSelectedMetricId] = useState(
    String(metricIdProp || "").trim()
  );
  const searchRef = useRef(null);
  const selectingRef = useRef(false);
  const loadedCategoriesRef = useRef({});

  const uomValue = String(value || "").trim();
  const categoryValue = String(metricCategoryProp || selectedCategory || "").trim();

  useEffect(() => {
    setSelectedCategory(String(metricCategoryProp || "").trim());
    setSelectedMetricId(String(metricIdProp || "").trim());
  }, [metricCategoryProp, metricIdProp, uomValue]);

  useEffect(() => {
    let cancelled = false;
    setCategoriesLoading(true);
    setCategoriesError("");
    fetchMetricCategories()
      .then((list) => {
        if (!cancelled) setCategories(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setCategories([]);
        setCategoriesError(err?.message || "Unable to load units of measure.");
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Prefetch + expand saved category when reopening an item (MetricTreeSelect behaviour).
  useEffect(() => {
    if (!categoryValue) return undefined;
    setExpanded((prev) => ({ ...prev, [categoryValue]: true }));
    if (loadedCategoriesRef.current[categoryValue]) return undefined;

    let cancelled = false;
    loadedCategoriesRef.current[categoryValue] = true;
    setLoadingCategory(categoryValue);
    fetchUomOptionsByCategory(categoryValue, { size: 100 })
      .then((list) => {
        if (cancelled) return;
        const next = Array.isArray(list) ? list : [];
        setChildrenByCategory((prev) => ({ ...prev, [categoryValue]: next }));
        setSelectedMetricId((current) => {
          if (current) return current;
          if (!uomValue) return "";
          const matched = next.find((metric) => metricMatchesUom(metric, uomValue));
          return matched?.id != null ? String(matched.id) : "";
        });
      })
      .catch((err) => {
        if (cancelled) return;
        loadedCategoriesRef.current[categoryValue] = false;
        setChildError(err?.message || "Unable to load units for this category.");
      })
      .finally(() => {
        if (!cancelled) setLoadingCategory(null);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryValue, uomValue]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    selectingRef.current = false;
    const timer = window.setTimeout(() => searchRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [open]);

  const selectedLabel = useMemo(() => {
    if (selectedMetricId) {
      for (const list of Object.values(childrenByCategory)) {
        const hit = (list || []).find((metric) => String(metric.id) === String(selectedMetricId));
        if (hit) return hit.label || uomValueFromMetric(hit);
      }
    }
    if (uomValue) {
      for (const list of Object.values(childrenByCategory)) {
        const hit = (list || []).find((metric) => metricMatchesUom(metric, uomValue));
        if (hit) return hit.label || uomValueFromMetric(hit);
      }
      return uomValue;
    }
    return "";
  }, [childrenByCategory, selectedMetricId, uomValue]);

  const filteredCategories = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return categories;
    return categories.filter((name) => {
      if (String(name).toLowerCase().includes(term)) return true;
      const children = childrenByCategory[name] || [];
      return children.some((metric) => {
        const haystack = `${metric.label || ""} ${metric.name || ""} ${metric.abbreviation || ""}`
          .toLowerCase();
        return haystack.includes(term);
      });
    });
  }, [categories, childrenByCategory, query]);

  async function loadCategoryChildren(cat) {
    if (!cat || loadedCategoriesRef.current[cat]) return;
    setLoadingCategory(cat);
    setChildError("");
    loadedCategoriesRef.current[cat] = true;
    try {
      const list = await fetchUomOptionsByCategory(cat, { size: 100 });
      setChildrenByCategory((prev) => ({ ...prev, [cat]: list || [] }));
    } catch (err) {
      loadedCategoriesRef.current[cat] = false;
      setChildError(err?.message || "Unable to load units for this category.");
      setChildrenByCategory((prev) => ({ ...prev, [cat]: [] }));
    } finally {
      setLoadingCategory(null);
    }
  }

  async function toggleCategory(cat) {
    const isOpen = !!expanded[cat];
    setExpanded((prev) => ({ ...prev, [cat]: !isOpen }));
    if (isOpen) return;
    await loadCategoryChildren(cat);
  }

  // While searching, expand matching categories so child units stay reachable.
  useEffect(() => {
    if (!open) return;
    const term = query.trim().toLowerCase();
    if (!term) return;
    filteredCategories.forEach((cat) => {
      setExpanded((prev) => (prev[cat] ? prev : { ...prev, [cat]: true }));
      void loadCategoryChildren(cat);
    });
  }, [open, query, filteredCategories]);

  function emitChange({ uom, metricCategory, metricId, label }) {
    onChange?.({
      uom: String(uom || "").trim(),
      metricCategory: String(metricCategory || "").trim(),
      metricId: metricId != null && metricId !== "" ? String(metricId) : "",
      label: String(label || uom || "").trim(),
    });
  }

  function handleSelectMetric(cat, metric) {
    if (selectingRef.current) return;
    selectingRef.current = true;
    const nextUom = uomValueFromMetric(metric);
    const nextId = metric?.id != null ? String(metric.id) : "";
    setSelectedCategory(cat);
    setSelectedMetricId(nextId);
    setOpen(false);
    setQuery("");
    emitChange({
      uom: nextUom,
      metricCategory: cat,
      metricId: nextId,
      label: metric?.label || nextUom,
    });
    window.setTimeout(() => {
      selectingRef.current = false;
    }, 0);
  }

  function handleClear() {
    if (selectingRef.current) return;
    selectingRef.current = true;
    setSelectedCategory("");
    setSelectedMetricId("");
    setOpen(false);
    setQuery("");
    emitChange({
      uom: "",
      metricCategory: "",
      metricId: "",
      label: "",
    });
    window.setTimeout(() => {
      selectingRef.current = false;
    }, 0);
  }

  if (!editable) {
    return (
      <div className="flex min-h-[40px] items-center px-2 text-[13px] text-[#34445f]">
        {selectedLabel || "-"}
      </div>
    );
  }

  return (
    <>
      <div className="rounded-[12px] border border-[#d1d9e8] bg-white px-1 py-1">
        <button
          type="button"
          disabled={categoriesLoading}
          onClick={() => setOpen(true)}
          className="flex min-h-[40px] w-full items-center justify-between gap-2 rounded-[10px] bg-white px-2 text-left text-[13px] text-[#34445f] disabled:cursor-not-allowed disabled:text-[#8ea0bd]"
        >
          <span className="min-w-0 truncate">
            {categoriesLoading
              ? "Loading..."
              : selectedLabel ||
                (categories.length === 0 ? "No UOM categories available" : "Select unit of measure")}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-[#8ea0bd]" />
        </button>
      </div>

      {categoriesError ? (
        <p className="mt-1 text-[11px] text-[#b04d43]">{categoriesError}</p>
      ) : null}

      <MobileSearchSheet
        open={open}
        title="Unit of Measure"
        searchPlaceholder="Search category or unit..."
        query={query}
        onQueryChange={setQuery}
        searchRef={searchRef}
        onClose={() => {
          if (!selectingRef.current) setOpen(false);
        }}
        localizedFieldProps={localizedFieldProps}
      >
        {categoriesLoading ? (
          <p className="px-1 py-6 text-center text-[13px] text-[#6f82a1]">Loading...</p>
        ) : null}
        {!categoriesLoading && filteredCategories.length === 0 ? (
          <p className="px-1 py-6 text-center text-[13px] text-[#6f82a1]">
            {query.trim() ? "No matching units." : "No metric categories from API"}
          </p>
        ) : null}

        <div className="space-y-2">
          {selectedLabel || selectedMetricId || uomValue ? (
            <MobilePickerOption label="Clear selection" muted onSelect={handleClear} />
          ) : null}

          {filteredCategories.map((cat) => {
            const isExpanded = !!expanded[cat];
            const children = childrenByCategory[cat] || [];
            const isLoading = loadingCategory === cat;
            const term = query.trim().toLowerCase();
            const visibleChildren = term
              ? children.filter((metric) => {
                  const haystack =
                    `${metric.label || ""} ${metric.name || ""} ${metric.abbreviation || ""}`.toLowerCase();
                  return haystack.includes(term) || String(cat).toLowerCase().includes(term);
                })
              : children;

            return (
              <div
                key={cat}
                className="overflow-hidden rounded-[12px] border border-[#d1d9e8] bg-white"
              >
                <button
                  type="button"
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={() => {
                    void toggleCategory(cat);
                  }}
                  className="flex min-h-[48px] w-full items-center gap-2 px-3 py-2.5 text-left text-[14px] font-semibold text-[#23324c]"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-[#8ea0bd]" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-[#8ea0bd]" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{cat}</span>
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#8ea0bd]" />
                  ) : null}
                </button>

                {isExpanded ? (
                  <div className="space-y-1.5 border-t border-[#e8eef7] bg-[#f7f9fc] px-2 py-2">
                    {!isLoading && visibleChildren.length === 0 ? (
                      <p className="px-2 py-2 text-[12px] text-[#8ea0bd]">No metrics in category</p>
                    ) : null}
                    {visibleChildren.map((metric) => {
                      const id = String(metric.id || "");
                      const selected =
                        (selectedMetricId && id === String(selectedMetricId)) ||
                        metricMatchesUom(metric, uomValue);
                      return (
                        <MobilePickerOption
                          key={id || metric.label}
                          label={metric.label || metric.name || id}
                          selected={Boolean(selected)}
                          onSelect={() => handleSelectMetric(cat, metric)}
                        />
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {childError ? (
          <p className="mt-3 px-1 text-[12px] text-[#b04d43]">{childError}</p>
        ) : null}
      </MobileSearchSheet>
    </>
  );
}

/**
 * Mobile Working Sheet: system accordion + item cards + full-field bottom sheet.
 * Desktop table stays in WorkingSheetTab — this is presentation only.
 * Flow: Add System (FSM products-services) → Add line under that system.
 */
export function QuotationWorkingSheetMobile({
  editable,
  saving,
  showInstallation,
  showSystemHeaders,
  systemSections,
  rows = [],
  makes,
  makesLoading,
  makesError,
  formatMoney,
  isSystemSectionExpanded,
  isSystemSectionLoading,
  isSystemSectionLoaded,
  systemLoadErrors,
  getSystemSectionKey,
  onToggleSystem,
  onAddRowForSystem,
  onAddSystem,
  onUpdateRow,
  onRemoveRow,
  onUpdateBrand,
  onUpdatePartNo,
  onApplyProduct,
  MakeField: _MakeField,
  PartField: _PartField,
  localizedFieldProps,
}) {
  const [editingRowId, setEditingRowId] = useState(null);
  const [systemPickerOpen, setSystemPickerOpen] = useState(false);
  const [systemOptions, setSystemOptions] = useState([]);
  const [systemPage, setSystemPage] = useState(0);
  const [systemHasMore, setSystemHasMore] = useState(false);
  const [systemsLoading, setSystemsLoading] = useState(false);
  const [systemsLoadingMore, setSystemsLoadingMore] = useState(false);
  const [systemsError, setSystemsError] = useState("");
  const [addingSystemId, setAddingSystemId] = useState("");

  function toNumber(value) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const namedSections = useMemo(
    () =>
      (systemSections || []).filter(
        (section) =>
          Boolean(String(section?.name || "").trim()) &&
          section.id !== "default" &&
          section.id !== "unassigned"
      ),
    [systemSections]
  );

  const existingSystemKeys = useMemo(() => {
    const keys = new Set();
    namedSections.forEach((section) => {
      const id = String(section.id || "").trim();
      const name = String(section.name || "").trim().toLowerCase();
      if (id) keys.add(`id:${id}`);
      if (name) keys.add(`name:${name}`);
    });
    return keys;
  }, [namedSections]);

  const rowsById = useMemo(() => {
    const map = new Map();
    (Array.isArray(rows) ? rows : []).forEach((row) => {
      map.set(String(row.id), row);
    });
    return map;
  }, [rows]);

  // Prepared row for computed amounts; raw row for editable inputs (same contract as desktop).
  const editingPreparedRow = useMemo(() => {
    if (!editingRowId) return null;
    for (const section of systemSections) {
      const found = section.rows.find((row) => String(row.id) === String(editingRowId));
      if (found) return found;
    }
    return null;
  }, [editingRowId, systemSections]);

  const editingRawRow = useMemo(() => {
    if (!editingRowId) return null;
    return rowsById.get(String(editingRowId)) || editingPreparedRow;
  }, [editingRowId, rowsById, editingPreparedRow]);

  const editingRow = editingPreparedRow;

  const incompleteCount = useMemo(() => {
    let count = 0;
    systemSections.forEach((section) => {
      section.rows.forEach((row) => {
        if (!toNumber(row.qty) || !toNumber(row.unitRate)) count += 1;
      });
    });
    return count;
  }, [systemSections]);

  function sellingSupplyAmount(row) {
    return row.numericTotals?.sellingSupplyAmount ?? toNumber(row.amount);
  }

  function lineSellingTotal(row) {
    const supply = sellingSupplyAmount(row);
    const install = showInstallation
      ? (row.numericTotals?.sellingInstallationAmount ?? toNumber(row.installationAmount))
      : 0;
    return supply + install;
  }

  function bumpQty(row, delta) {
    if (!editable || saving) return;
    const current = toNumber(row.qty);
    const next = Math.max(0, current + delta);
    onUpdateRow(row.id, "qty", String(next));
  }

  function closeEditor() {
    setEditingRowId(null);
  }

  function isSystemAlreadyAdded(option) {
    const id = String(option?.id || option?.productServiceId || "").trim();
    const name = String(option?.name || "").trim().toLowerCase();
    if (id && existingSystemKeys.has(`id:${id}`)) return true;
    if (name && existingSystemKeys.has(`name:${name}`)) return true;
    return false;
  }

  async function loadSystemOptions({ page = 0, append = false } = {}) {
    if (append) setSystemsLoadingMore(true);
    else setSystemsLoading(true);
    setSystemsError("");
    try {
      const result = await searchProductServicesPage({
        page,
        size: 10,
        sortBy: "productServiceId",
        sortDir: "ASC",
      });
      setSystemOptions((current) => (append ? [...current, ...(result.items || [])] : result.items || []));
      setSystemPage(result.page ?? page);
      setSystemHasMore(Boolean(result.hasMore));
    } catch (error) {
      setSystemsError(error?.message || sq("mobileSystemsLoadError"));
      if (!append) {
        setSystemOptions([]);
        setSystemHasMore(false);
      }
    } finally {
      setSystemsLoading(false);
      setSystemsLoadingMore(false);
    }
  }

  useEffect(() => {
    if (!systemPickerOpen) return undefined;
    let active = true;
    (async () => {
      if (!active) return;
      await loadSystemOptions({ page: 0, append: false });
    })();
    return () => {
      active = false;
    };
  }, [systemPickerOpen]);

  function openSystemPicker() {
    if (!editable || saving || typeof onAddSystem !== "function") return;
    setSystemPickerOpen(true);
  }

  function closeSystemPicker() {
    setSystemPickerOpen(false);
    setAddingSystemId("");
    setSystemsError("");
  }

  async function handleSelectSystem(option) {
    if (!editable || saving || typeof onAddSystem !== "function") return;
    if (isSystemAlreadyAdded(option)) return;
    const systemId = String(option.id || option.productServiceId || "").trim();
    const systemName = String(option.name || "").trim();
    if (!systemId || !systemName) return;

    setAddingSystemId(systemId);
    try {
      await onAddSystem({
        id: systemId,
        name: systemName,
        productServiceId: option.productServiceId ?? systemId,
      });
      closeSystemPicker();
    } catch (error) {
      setSystemsError(error?.message || sq("mobileSystemsLoadError"));
    } finally {
      setAddingSystemId("");
    }
  }

  function renderSystemSection(section, { forceNamed = false } = {}) {
    const isNamedSystem = forceNamed || Boolean(String(section.name || "").trim());
    const sectionKey = getSystemSectionKey(section);
    const expanded =
      !showSystemHeaders || !isNamedSystem || isSystemSectionExpanded(section);
    const loading = showSystemHeaders && isNamedSystem && isSystemSectionLoading(section);
    const loaded = !showSystemHeaders || !isNamedSystem || isSystemSectionLoaded(section);
    const sectionError = systemLoadErrors[sectionKey] || "";
    const incompleteInSection = section.rows.filter(
      (row) => !toNumber(row.qty) || !toNumber(row.unitRate)
    ).length;
    const sectionSell = section.rows.reduce((sum, row) => sum + lineSellingTotal(row), 0);

    return (
      <div
        key={section.id || section.name || "rows"}
        className="overflow-hidden rounded-[12px] border border-[#d8e0ed] bg-white"
      >
        {showSystemHeaders && isNamedSystem ? (
          <div className="flex items-center gap-1 border-b border-[#e4eaf4] bg-[#f7f9fc] px-2 py-1">
            <button
              type="button"
              onClick={() => onToggleSystem(section)}
              disabled={loading}
              className="flex min-h-[48px] min-w-0 flex-1 items-center gap-2 px-2 text-left disabled:cursor-wait"
            >
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[8px] bg-[#dce6ff] text-[#2944a8]">
                <Zap className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-semibold text-[#23324c]">{section.name}</p>
                <p className="text-[12px] text-[#6f82a1]">
                  {loading
                    ? sq("mobileSystemsLoading")
                    : `${section.rows.length} items · ${formatMoney(sectionSell)}`}
                </p>
              </div>
              {incompleteInSection > 0 ? (
                <span className="rounded-full bg-[#fdefd0] px-2 py-0.5 text-[11px] font-semibold text-[#965800]">
                  {incompleteInSection}
                </span>
              ) : null}
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-[#6f82a1] transition ${expanded ? "" : "-rotate-90"}`}
              />
            </button>
          </div>
        ) : null}

        {sectionError ? (
          <p className="bg-[#fff6f5] px-3 py-2 text-[11px] font-medium text-[#b04d43]">{sectionError}</p>
        ) : null}

        {expanded ? (
          <div className="space-y-2 bg-[#f7f9fc] p-2.5">
            {!loaded && !loading && showSystemHeaders && isNamedSystem ? (
              <p className="px-1 py-2 text-[12px] text-[#6f82a1]">{sq("mobileTapSystemToLoad")}</p>
            ) : null}
            {section.rows.map((row) => {
              const qtyMissing = !toNumber(row.qty);
              const sellMissing = !toNumber(row.unitRate);
              return (
                <div key={row.id} className="rounded-[12px] border border-[#d8e0ed] bg-white p-3">
                  <button
                    type="button"
                    onClick={() => setEditingRowId(row.id)}
                    className="w-full text-left"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[12px] text-[#6f82a1]">
                        {[row.slNo, row.make, row.partNo].filter(Boolean).join(" · ") || "New item"}
                      </p>
                      {qtyMissing || sellMissing ? (
                        <span className="shrink-0 rounded-full bg-[#fdefd0] px-2 py-0.5 text-[11px] font-semibold text-[#965800]">
                          {sellMissing ? "Add rate" : "Add qty"}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-[#2d394c]">
                      {row.description || "No description"}
                    </p>
                  </button>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="inline-flex items-center overflow-hidden rounded-[10px] border border-[#d1d9e8]">
                      <button
                        type="button"
                        aria-label="Decrease quantity"
                        disabled={!editable || saving}
                        onClick={() => bumpQty(row, -1)}
                        className="grid h-10 w-10 place-items-center text-[18px] font-semibold text-[#2944a8] disabled:text-[#a6b3d6]"
                      >
                        −
                      </button>
                      <span className="min-w-[36px] text-center text-[13px] font-semibold text-[#23324c]">
                        {row.qty || 0}
                      </span>
                      <button
                        type="button"
                        aria-label="Increase quantity"
                        disabled={!editable || saving}
                        onClick={() => bumpQty(row, 1)}
                        className="grid h-10 w-10 place-items-center text-[18px] font-semibold text-[#2944a8] disabled:text-[#a6b3d6]"
                      >
                        +
                      </button>
                    </div>
                    <div className="text-right">
                      <p className="text-[12px] text-[#6f82a1]">× {formatMoney(toNumber(row.unitRate))}</p>
                      <p className="text-[15px] font-semibold text-[#23324c]">
                        {formatMoney(sellingSupplyAmount(row))}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            {isNamedSystem ? (
              <button
                type="button"
                onClick={() => onAddRowForSystem(section)}
                disabled={!editable || saving || loading}
                className="inline-flex min-h-[42px] w-full items-center justify-center gap-1.5 rounded-[12px] bg-[#eef3fb] text-[13px] font-semibold text-[#2944a8] disabled:cursor-not-allowed disabled:text-[#a6b3d6]"
              >
                <Plus className="h-4 w-4" />
                {sq("mobileAddItem")}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {!editable && (
        <div className="flex items-start gap-2.5 rounded-[12px] border border-[#e4eaf4] bg-[#f8fbff] px-3 py-2 text-[#6a7f9f]">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="text-[12px] font-semibold text-[#51657f]">{sq("readonlyTitle")}</p>
            <p className="mt-0.5 text-[11px] leading-5">{sq("readonlyHint")}</p>
          </div>
        </div>
      )}

      {makesError ? <p className="text-[11px] font-medium text-[#b35b62]">{makesError}</p> : null}

      {incompleteCount > 0 ? (
        <p className="rounded-[10px] border border-[#fdefd0] bg-[#fffaf0] px-3 py-2 text-[12px] text-[#965800]">
          {incompleteCount} item{incompleteCount === 1 ? "" : "s"} need a quantity or selling rate
        </p>
      ) : null}

      {!showSystemHeaders ? (
        <div className="rounded-[12px] border border-[#d8e0ed] bg-white px-4 py-5">
          <p className="text-[14px] font-semibold text-[#23324c]">{sq("mobileNoSystemsYet")}</p>
          <p className="mt-1 text-[13px] leading-5 text-[#6f82a1]">{sq("mobileNoSystemsHint")}</p>
        </div>
      ) : null}

      <div className="space-y-2.5">
        {namedSections.map((section) => renderSystemSection(section, { forceNamed: true }))}
      </div>

      {editable ? (
        <button
          type="button"
          onClick={openSystemPicker}
          disabled={saving || typeof onAddSystem !== "function"}
          className="inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-[12px] border border-[#d0d9ea] bg-white text-[13px] font-semibold text-[#2944a8] disabled:cursor-not-allowed disabled:text-[#a6b3d6]"
        >
          <Plus className="h-4 w-4" />
          {sq("addSystem")}
        </button>
      ) : null}

      {systemPickerOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end bg-black/40"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeSystemPicker();
          }}
        >
          <div className="flex max-h-[85dvh] w-full flex-col rounded-t-[20px] bg-[#f4f6fb]">
            <div className="flex items-center justify-between gap-2 border-b border-[#e2e4f0] px-4 py-3">
              <p className="text-[16px] font-semibold text-[#23324c]">{sq("mobileSelectSystem")}</p>
              <button
                type="button"
                onClick={closeSystemPicker}
                className="grid h-10 w-10 place-items-center rounded-full text-[#6f82a1]"
                aria-label={sq("close")}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
              {systemsError ? (
                <p className="mb-3 rounded-[10px] border border-[#f4cfcb] bg-[#fff6f5] px-3 py-2 text-[12px] text-[#b04d43]">
                  {systemsError}
                </p>
              ) : null}
              {systemsLoading && systemOptions.length === 0 ? (
                <p className="px-1 py-6 text-center text-[13px] text-[#6f82a1]">{sq("mobileSystemsLoading")}</p>
              ) : null}
              {!systemsLoading && systemOptions.length === 0 && !systemsError ? (
                <p className="px-1 py-6 text-center text-[13px] text-[#6f82a1]">{sq("mobileSystemsEmpty")}</p>
              ) : null}
              <div className="space-y-2">
                {systemOptions.map((option) => {
                  const alreadyAdded = isSystemAlreadyAdded(option);
                  const optionId = String(option.id || option.productServiceId || "");
                  const busy = addingSystemId && String(addingSystemId) === optionId;
                  return (
                    <button
                      key={optionId || option.name}
                      type="button"
                      disabled={alreadyAdded || Boolean(addingSystemId) || saving}
                      onClick={() => handleSelectSystem(option)}
                      className={`block w-full rounded-[12px] border px-4 py-3.5 text-left transition ${
                        alreadyAdded
                          ? "cursor-not-allowed border-[#e4e9f2] bg-[#f7f9fc] opacity-70"
                          : "border-[#d1d9e8] bg-white active:bg-[#f7f9ff]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[14px] font-semibold text-[#23324c]">{option.name}</p>
                          {alreadyAdded ? (
                            <p className="mt-1 text-[12px] text-[#8ea0bd]">{sq("mobileSystemAlreadyAdded")}</p>
                          ) : null}
                        </div>
                        <span className="mt-0.5 text-[12px] font-semibold text-[#2944a8]">
                          {busy ? "…" : alreadyAdded ? "✓" : "+"}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {systemHasMore ? (
                <button
                  type="button"
                  onClick={() => loadSystemOptions({ page: systemPage + 1, append: true })}
                  disabled={systemsLoadingMore || systemsLoading}
                  className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center rounded-[12px] border border-[#d0d9ea] bg-white text-[13px] font-semibold text-[#2944a8] disabled:text-[#a6b3d6]"
                >
                  {systemsLoadingMore ? sq("mobileSystemsLoading") : sq("mobileLoadMoreSystems")}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {editingRow && editingRawRow ? (
        <div
          className="fixed inset-0 z-50 flex items-end bg-black/40"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeEditor();
          }}
        >
          <div className="flex max-h-[92dvh] w-full flex-col rounded-t-[20px] bg-[#f2f3f9]">
            <div className="flex items-center justify-between gap-2 border-b border-[#e2e4f0] px-4 py-3">
              <p className="text-[16px] font-semibold text-[#23324c]">
                {editingRawRow.slNo ? `Item ${editingRawRow.slNo}` : "Edit item"}
              </p>
              <button
                type="button"
                onClick={closeEditor}
                className="grid h-10 w-10 place-items-center rounded-full text-[#6f82a1]"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <Field label="Description">
                <Textarea
                  value={editingRawRow.description || ""}
                  disabled={!editable || saving}
                  onChange={(event) => onUpdateRow(editingRawRow.id, "description", event.target.value)}
                  className="min-h-[88px] rounded-[12px] border-[#d1d9e8] text-[13px]"
                  rows={2} {...localizedFieldProps?.()}
                />
              </Field>
              <Field label="Sl No">
                <input
                  value={editingRawRow.slNo || ""}
                  disabled={!editable || saving}
                  onChange={(event) => onUpdateRow(editingRawRow.id, "slNo", event.target.value)}
                  className={mobileInputClass(!editable)}
                  {...localizedFieldProps?.()}
                />
              </Field>
              <Field label="Unit of Measure">
                <MobileUomField
                  value={editingRawRow.uom}
                  metricCategory={editingRawRow.metricCategory}
                  metricId={editingRawRow.metricId}
                  editable={editable && !saving}
                  onChange={({ uom, metricCategory, metricId }) => {
                    onUpdateRow(editingRawRow.id, "uom", uom);
                    onUpdateRow(editingRawRow.id, "metricCategory", metricCategory);
                    onUpdateRow(editingRawRow.id, "metricId", metricId);
                  }}
                  localizedFieldProps={localizedFieldProps}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Make">
                  <div className="rounded-[12px] border border-[#d1d9e8] bg-white px-1 py-1">
                    <MobileMakeField
                      value={editingRawRow.make}
                      makes={makes}
                      makesLoading={makesLoading}
                      editable={editable && !saving}
                      onChange={(value) => onUpdateBrand(editingRawRow.id, value)}
                      localizedFieldProps={localizedFieldProps}
                    />
                  </div>
                </Field>
                <Field label="Part No">
                  <div className="rounded-[12px] border border-[#d1d9e8] bg-white px-1 py-1">
                    <MobilePartNoField
                      value={editingRawRow.partNo}
                      make={editingRawRow.make}
                      editable={editable && !saving}
                      onChange={(value) => onUpdatePartNo(editingRawRow.id, value)}
                      onProductSelect={(product, options) =>
                        onApplyProduct(editingRawRow.id, product, options)
                      }
                      localizedFieldProps={localizedFieldProps}
                    />
                  </div>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Quantity">
                  <input
                    inputMode="decimal"
                    value={editingRawRow.qty ?? ""}
                    disabled={!editable || saving}
                    onChange={(event) => onUpdateRow(editingRawRow.id, "qty", event.target.value)}
                    className={mobileInputClass(!editable)}
                    {...localizedFieldProps?.()}
                  />
                </Field>
                <Field label="Special Disc %">
                  <input
                    inputMode="decimal"
                    value={editingRawRow.discount ?? ""}
                    disabled={!editable || saving}
                    onChange={(event) => onUpdateRow(editingRawRow.id, "discount", event.target.value)}
                    className={mobileInputClass(!editable)}
                    {...localizedFieldProps?.()}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="List Price">
                  <input
                    inputMode="decimal"
                    value={editingRawRow.listPrice ?? ""}
                    disabled={!editable || saving}
                    onChange={(event) => onUpdateRow(editingRawRow.id, "listPrice", event.target.value)}
                    className={mobileInputClass(!editable)}
                    {...localizedFieldProps?.()}
                  />
                </Field>
                <Field label="Net Price">
                  <input
                    inputMode="decimal"
                    value={editingRawRow.netPrice ?? ""}
                    disabled={!editable || saving}
                    onChange={(event) => onUpdateRow(editingRawRow.id, "netPrice", event.target.value)}
                    className={mobileInputClass(!editable)}
                    {...localizedFieldProps?.()}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Input Supp. Rate">
                  <input
                    inputMode="decimal"
                    value={
                      editingRawRow.initialUnitRateManual ||
                      String(editingRawRow.initialUnitRate ?? "").trim() !== ""
                        ? (editingRawRow.initialUnitRate ?? "")
                        : (editingRow.initialUnitRate ?? "")
                    }
                    disabled={!editable || saving}
                    onChange={(event) =>
                      onUpdateRow(editingRawRow.id, "initialUnitRate", event.target.value)
                    }
                    className={mobileInputClass(!editable)}
                    {...localizedFieldProps?.()}
                  />
                </Field>
                <Field label="Input Supp. Amt">
                  <input
                    readOnly
                    value={formatMoney(
                      editingRow.numericTotals?.initialAmount ?? toNumber(editingRow.initialAmount)
                    )}
                    className={mobileInputClass(true)}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Mult / Disc">
                  <FormSelect
                    value={
                      String(editingRawRow.adjustmentType || "") === "Discount %"
                        ? "Discount %"
                        : "Multiplier"
                    }
                    disabled={!editable || saving}
                    onChange={(event) =>
                      onUpdateRow(editingRawRow.id, "adjustmentType", event.target.value)
                    }
                    className={mobileInputClass(!editable)}
                    {...localizedFieldProps?.()}
                  >
                    <option value="Multiplier">Multiplier</option>
                    <option value="Discount %">Discount %</option>
                  </FormSelect>
                </Field>
                <Field label="Mult / Disc Rate">
                  <input
                    inputMode="decimal"
                    value={
                      editingRawRow.adjustmentRateManual ||
                      String(editingRawRow.adjustmentRate ?? "").trim() !== ""
                        ? (editingRawRow.adjustmentRate ?? "")
                        : (editingRow.adjustmentRate ?? editingRow.multiplier ?? "")
                    }
                    disabled={!editable || saving}
                    onChange={(event) =>
                      onUpdateRow(editingRawRow.id, "adjustmentRate", event.target.value)
                    }
                    className={mobileInputClass(!editable)}
                    {...localizedFieldProps?.()}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Selling Supp. Rate">
                  <input
                    inputMode="decimal"
                    value={
                      editingRawRow.unitRateManual ||
                      String(editingRawRow.unitRate ?? "").trim() !== ""
                        ? (editingRawRow.unitRate ?? "")
                        : (editingRow.unitRate ?? "")
                    }
                    disabled={!editable || saving}
                    onChange={(event) => onUpdateRow(editingRawRow.id, "unitRate", event.target.value)}
                    className={mobileInputClass(!editable)}
                    {...localizedFieldProps?.()}
                  />
                </Field>
                <Field label="Selling Supp. Amt">
                  <input
                    readOnly
                    value={formatMoney(
                      editingRow.numericTotals?.sellingSupplyAmount ?? toNumber(editingRow.amount)
                    )}
                    className={mobileInputClass(true)}
                  />
                </Field>
              </div>

              {showInstallation ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Install / U">
                      <input
                        inputMode="decimal"
                        value={editingRawRow.installationPerUnit ?? ""}
                        disabled={!editable || saving}
                        onChange={(event) =>
                          onUpdateRow(editingRawRow.id, "installationPerUnit", event.target.value)
                        }
                        className={mobileInputClass(!editable)}
                        {...localizedFieldProps?.()}
                      />
                    </Field>
                    <Field label="Install Amt">
                      <input
                        readOnly
                        value={formatMoney(
                          editingRow.numericTotals?.inputInstallationAmount ??
                            toNumber(editingRow.inputInstallationAmount)
                        )}
                        className={mobileInputClass(true)}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Install Mult.">
                      <input
                        inputMode="decimal"
                        value={
                          editingRawRow.installationMultiplierManual ||
                          String(editingRawRow.installationMultiplier ?? "").trim() !== ""
                            ? (editingRawRow.installationMultiplier ?? "")
                            : (editingRow.installationMultiplier ?? "")
                        }
                        disabled={!editable || saving}
                        onChange={(event) =>
                          onUpdateRow(editingRawRow.id, "installationMultiplier", event.target.value)
                        }
                        className={mobileInputClass(!editable)}
                        {...localizedFieldProps?.()}
                      />
                    </Field>
                    <Field label="Selling Install Rate">
                      <input
                        readOnly
                        value={formatMoney(
                          editingRow.numericTotals?.sellingInstallationRate ??
                            toNumber(editingRow.sellingInstallationRate)
                        )}
                        className={mobileInputClass(true)}
                      />
                    </Field>
                  </div>
                  <Field label="Selling Install Amt">
                    <input
                      readOnly
                      value={formatMoney(
                        editingRow.numericTotals?.sellingInstallationAmount ??
                          toNumber(editingRow.installationAmount)
                      )}
                      className={mobileInputClass(true)}
                    />
                  </Field>
                </>
              ) : null}

              <Field label="Remarks">
                <input
                  value={editingRawRow.remarks || ""}
                  disabled={!editable || saving}
                  onChange={(event) => onUpdateRow(editingRawRow.id, "remarks", event.target.value)}
                  className={mobileInputClass(!editable)}
                  {...localizedFieldProps?.()}
                />
              </Field>

              <div className="mb-2 flex items-center justify-between rounded-[12px] border border-[#d8e0ed] bg-white px-3 py-3">
                <div>
                  <p className="text-[12px] text-[#6f82a1]">Selling Supply Amt</p>
                  <p className="text-[17px] font-semibold text-[#23324c]">
                    {formatMoney(sellingSupplyAmount(editingRow))}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 border-t border-[#e2e4f0] bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              {editable ? (
                <button
                  type="button"
                  onClick={() => {
                    onRemoveRow(editingRawRow.id);
                    closeEditor();
                  }}
                  className="inline-flex min-h-[46px] items-center justify-center gap-1.5 rounded-[12px] px-3 text-[13px] font-semibold text-[#c74b40]"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              ) : null}
              <button
                type="button"
                onClick={closeEditor}
                className="inline-flex min-h-[46px] flex-1 items-center justify-center rounded-[12px] bg-[#243c93] text-[13px] font-semibold text-white"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
